import React from 'react';
import { formatCedi } from '../utils/formatters';
import { buildAlertSummary } from '../utils/orderAlert';

/**
 * The banner raised when website orders arrive.
 *
 * Stays on screen until somebody explicitly taps it — deliberately not a toast.
 * The whole point of the feature is surviving "I was in the back for two
 * minutes", which anything self-dismissing cannot do.
 */
const NewOrderAlert = ({ orders, isRinging, rungOut, onAcknowledge, onView }) => {
  if (!orders || orders.length === 0) return null;

  const { count, title, lines } = buildAlertSummary(orders);

  return (
    <div
      // assertive, not polite: this is the one notification in the app that is
      // allowed to interrupt whatever a screen reader is currently saying.
      role="alert"
      aria-live="assertive"
      className="fixed inset-x-0 top-0 z-[9990] px-3 pt-3 pointer-events-none"
    >
      <div
        className={`pointer-events-auto mx-auto w-full max-w-2xl rounded-2xl border-2 shadow-2xl overflow-hidden ${
          isRinging && !rungOut
            ? 'border-emerald-500 bg-emerald-50 animate-pulse'
            : 'border-amber-500 bg-amber-50'
        }`}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="text-3xl leading-none" aria-hidden="true">
            {rungOut ? '🔔' : '🛎️'}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {count > 1 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-900 text-white">
                  {count}
                </span>
              )}
            </div>

            <ul className="mt-2 space-y-1">
              {lines.map((line) => (
                <li key={line.id} className="text-sm text-gray-800 flex flex-wrap gap-x-2">
                  <span className="font-mono font-semibold">{line.reference}</span>
                  <span className="truncate max-w-[12rem]">{line.customerName}</span>
                  {line.total !== null && (
                    <span className="font-semibold">{formatCedi(line.total)}</span>
                  )}
                  {line.paymentState && (
                    <span
                      className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        line.paymentState === 'Unpaid'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {line.paymentState}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            {rungOut && (
              <p className="mt-2 text-xs text-amber-800">
                Alarm stopped — still not acknowledged.
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 px-4 pb-4">
          <button
            type="button"
            onClick={onView}
            // Large tap target: this gets pressed by someone hurrying back to
            // the counter, often without looking closely.
            className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-semibold text-base active:scale-[0.99]"
          >
            View {count > 1 ? 'orders' : 'order'}
          </button>
          <button
            type="button"
            onClick={onAcknowledge}
            className="px-4 py-3 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold text-base active:scale-[0.99]"
          >
            Silence
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderAlert;
